import { useEffect, useRef } from "react";
import { FaPiggyBank, FaClock, FaShieldAlt, FaRegSmile, FaCheckCircle } from "react-icons/fa";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const WhyChooseUs = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const features = [
    {
      icon: <FaRegSmile />,
      stat: "100%",
      title: "Customer-Centric Approach",
      description: "We prioritize our clients' needs and provide personalized solutions to help them achieve their financial goals.",
      color: "from-blue-500 to-primary",
      lightBg: "bg-blue-50",
      iconBg: "bg-blue-100 text-primary",
    },
    {
      icon: <FaShieldAlt />,
      stat: "RBI",
      title: "Registered NBFC",
      description: "Satisfaction Commercial Pvt. Ltd. is a duly registered NBFC under the Reserve Bank of India — not a fintech.",
      color: "from-indigo-500 to-secondary",
      lightBg: "bg-indigo-50",
      iconBg: "bg-indigo-100 text-secondary",
    },
    {
      icon: <FaPiggyBank />,
      stat: "Low",
      title: "Affordable Interest Rates",
      description: "We offer competitive and transparent interest rates ensuring everyone has access to financial tools they need.",
      color: "from-sky-500 to-blue-600",
      lightBg: "bg-sky-50",
      iconBg: "bg-sky-100 text-sky-600",
    },
    {
      icon: <FaClock />,
      stat: "Fast",
      title: "Easy & Fast Application",
      description: "Our online loan application process is quick and hassle-free — apply from the comfort of your home.",
      color: "from-blue-400 to-primary",
      lightBg: "bg-blue-50",
      iconBg: "bg-blue-100 text-blue-600",
    },
  ];

  const trustPoints = [
    "Transparent Loan Terms",
    "No Hidden Charges",
    "Dedicated Grievance Redressal",
    "RBI Compliant Processes",
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".why-header > *", {
        y: 40,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".why-header",
          start: "top 85%",
        }
      });

      gsap.from(cardsRef.current.filter(Boolean), {
        y: 60,
        opacity: 0,
        duration: 0.6,
        stagger: 0.15,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: ".cards-grid",
          start: "top 80%",
        },
        clearProps: "all",
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="w-full relative">
      {/* Section Header */}
      <div className="why-header flex flex-col items-center text-center gap-5 mb-14 md:mb-20 px-4">
        <div className="px-5 py-2 bg-blue-50 text-primary border border-blue-100 text-xs font-bold uppercase tracking-widest rounded-full">
          Why Choose Us
        </div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-text-primary leading-tight tracking-tight">
          Built for <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Your Trust</span>
        </h2>
        <p className="text-text-secondary text-lg md:text-xl max-w-2xl font-medium leading-relaxed">
          As a RBI-registered NBFC, we deliver financial services with complete transparency, speed, and care.
        </p>

        {/* Trust Pills */}
        <div className="flex flex-wrap justify-center gap-3 mt-2">
          {trustPoints.map((point) => (
            <div key={point} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-100 rounded-full shadow-sm text-sm font-semibold text-text-primary">
              <FaCheckCircle className="text-primary text-xs" />
              {point}
            </div>
          ))}
        </div>
      </div>

      {/* Feature Cards */}
      <div className="cards-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 px-4">
        {features.map((item, index) => (
          <div
            key={index}
            ref={(el) => { cardsRef.current[index] = el; }}
            className="group relative bg-white rounded-3xl border border-gray-100 shadow-sm transition-all duration-500 hover:shadow-xl hover:-translate-y-2 overflow-hidden"
          >
            {/* Top Gradient Bar */}
            <div className={`h-1.5 w-full bg-gradient-to-r ${item.color}`} />

            <div className="p-8 flex flex-col h-full">
              {/* Icon + Stat Row */}
              <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 ${item.iconBg} rounded-2xl flex items-center justify-center text-2xl transition-all group-hover:scale-110`}>
                  {item.icon}
                </div>
                <span className={`text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br ${item.color}`}>
                  {item.stat}
                </span>
              </div>

              <h3 className="text-lg font-bold text-text-primary mb-3 leading-snug">{item.title}</h3>
              <p className="text-text-secondary text-sm leading-relaxed flex-grow">{item.description}</p>

              {/* Hover Indicator */}
              <div className="mt-6 h-0.5 w-full bg-gray-100 rounded-full overflow-hidden">
                <div className={`h-full w-0 group-hover:w-full transition-all duration-700 bg-gradient-to-r ${item.color} rounded-full`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WhyChooseUs;
